import { useState, useEffect } from "react";
import { Modal, TextInput, Button, Group, Alert, Checkbox } from "@mantine/core";
import { api, type Guest } from "../services/api";

interface EditGuestModalProps {
  opened: boolean;
  onClose: () => void;
  guest: Guest | null;
  onSuccess: () => void;
}

export function EditGuestModal({ opened, onClose, guest, onSuccess }: EditGuestModalProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isChild, setIsChild] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (guest) {
      setFirstName(guest.first_name);
      setLastName(guest.last_name);
      setIsChild(guest.is_child);
    }
  }, [guest]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!guest || !firstName.trim()) {
      setError("First name is required");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await api.updateGuest(guest.id, firstName.trim(), lastName.trim(), isChild);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update guest. Please try again.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Modal 
      opened={opened} 
      onClose={handleClose} 
      title="Edit Guest" 
      size="md"
      centered
    >
      <form onSubmit={handleSubmit}>
        {error && (
          <Alert color="red" style={{ marginBottom: "1rem" }}>
            {error}
          </Alert>
        )}

        <TextInput
          label="First Name"
          placeholder="e.g., John"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
          style={{ marginBottom: "1rem" }}
        />

        <TextInput
          label="Last Name (optional)"
          placeholder="e.g., Smith"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          style={{ marginBottom: "1rem" }}
        />

        <Checkbox
          label="Is this guest a child?"
          checked={isChild}
          onChange={(e) => setIsChild(e.currentTarget.checked)}
          style={{ marginBottom: "1.5rem" }}
        />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Updating..." : "Update Guest"}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
